import { Reorder, useDragControls, motion, useReducedMotion } from "framer-motion";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { bordaPreviewSummary } from "@voting/borda";
import { moveIdToOneBasedRank } from "@voting/rankedOrder";

type Opt = { id: number; content: string };

export { moveIdToOneBasedRank };

type RankedBallotProps = {
	options: Opt[];
	order: number[];
	onOrderChange: (next: number[]) => void;
	disabled: boolean;
	/** Increment when a new voting session starts (resets gesture gates). */
	ballotResetKey: number;
	onGestureEligibleChange: (eligible: boolean) => void;
};

function RankRow({
	o,
	disabled,
	onMove,
	index,
	total,
	draggingId,
	thudId,
	onItemDragStart,
	onItemDragEnd,
	reduceMotion,
	onDigitRank,
}: {
	o: Opt;
	disabled: boolean;
	onMove: (dir: -1 | 1) => void;
	index: number;
	total: number;
	draggingId: number | null;
	thudId: number | null;
	onItemDragStart: (id: number) => void;
	onItemDragEnd: () => void;
	reduceMotion: boolean;
	/** 1-based target rank while this row is focused (keys 1–9). */
	onDigitRank: (oneBased: number) => void;
}) {
	const dragControls = useDragControls();
	const neighborLift = draggingId !== null && draggingId !== o.id;

	return (
		<Reorder.Item
			value={o.id}
			as="li"
			tabIndex={disabled ? -1 : 0}
			dragListener={false}
			dragControls={dragControls}
			layout="position"
			transition={
				reduceMotion
					? { duration: 0 }
					: { type: "spring", stiffness: 300, damping: 30 }
			}
			onDragStart={() => onItemDragStart(o.id)}
			onDragEnd={onItemDragEnd}
			onKeyDown={(e: KeyboardEvent<HTMLLIElement>) => {
				if (disabled) return;
				if (e.key === "ArrowUp") {
					e.preventDefault();
					if (index > 0) onMove(-1);
				}
				if (e.key === "ArrowDown") {
					e.preventDefault();
					if (index < total - 1) onMove(1);
				}
				if (/^[1-9]$/.test(e.key)) {
					const r = Number(e.key);
					if (r >= 1 && r <= total) {
						e.preventDefault();
						onDigitRank(r);
					}
				}
			}}
			whileDrag={
				reduceMotion
					? undefined
					: {
							scale: 1.03,
							boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
							zIndex: 10,
						}
			}
			className="relative cursor-default rounded-xl border border-wi-text/10 bg-wi-surface px-3 py-2.5 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-wi-gold/35 focus-visible:ring-offset-2 focus-visible:ring-offset-wi-bg"
		>
			<motion.div
				animate={{
					y: reduceMotion ? 0 : neighborLift ? 2 : 0,
					scale: reduceMotion ? 1 : thudId === o.id ? [1, 1.06, 1] : 1,
				}}
				transition={{
					y: reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 28 },
					scale: reduceMotion ? { duration: 0 } : { duration: 0.24, times: [0, 0.45, 1] },
				}}
			>
				<div className="flex items-start gap-2">
					<button
						type="button"
						className="-ml-1 mt-0.5 inline-flex min-h-12 min-w-12 cursor-grab touch-none items-center justify-center rounded-lg text-wi-text/35 hover:bg-wi-text/[0.04] hover:text-wi-text/70 active:cursor-grabbing"
						aria-label={`Drag to reorder: ${o.content}`}
						disabled={disabled}
						onPointerDown={(e) => dragControls.start(e)}
					>
						⋮⋮
					</button>
					<div className="min-w-0 flex-1">
						<p className="font-medium text-sm text-wi-text leading-snug">{o.content}</p>
						<p className="mt-1 text-wi-text/45 text-xs">Rank {index + 1}</p>
					</div>
					<div className="flex shrink-0 flex-col gap-1.5">
						<button
							type="button"
							disabled={disabled || index === 0}
							className="min-h-12 min-w-[4.5rem] rounded-lg border border-wi-text/15 px-2 font-medium text-wi-text/75 text-xs hover:border-wi-gold/40 disabled:opacity-30"
							onClick={() => onMove(-1)}
						>
							Up
						</button>
						<button
							type="button"
							disabled={disabled || index >= total - 1}
							className="min-h-12 min-w-[4.5rem] rounded-lg border border-wi-text/15 px-2 font-medium text-wi-text/75 text-xs hover:border-wi-gold/40 disabled:opacity-30"
							onClick={() => onMove(1)}
						>
							Down
						</button>
					</div>
				</div>
			</motion.div>
		</Reorder.Item>
	);
}

const RANKED_KEY_CAPS = ["J / K", "↑ / ↓", "1–9", "⌘/Ctrl+Enter"] as const;

export function RankedBallot({
	options,
	order,
	onOrderChange,
	disabled,
	ballotResetKey,
	onGestureEligibleChange,
}: RankedBallotProps) {
	const reduceMotion = useReducedMotion() === true;
	const orderRef = useRef(order);
	orderRef.current = order;

	const dragSessionStart = useRef<number[] | null>(null);
	const [draggingId, setDraggingId] = useState<number | null>(null);
	const [thudId, setThudId] = useState<number | null>(null);
	const [dragGate, setDragGate] = useState(false);
	const [keyboardGate, setKeyboardGate] = useState(false);

	useEffect(() => {
		setDragGate(false);
		setKeyboardGate(false);
		setThudId(null);
		setDraggingId(null);
		dragSessionStart.current = null;
	}, [ballotResetKey]);

	useEffect(() => {
		onGestureEligibleChange(dragGate || keyboardGate);
	}, [dragGate, keyboardGate, onGestureEligibleChange]);

	const byId = useCallback(
		(id: number) => options.find((o) => o.id === id),
		[options],
	);

	const onItemDragStart = useCallback((id: number) => {
		dragSessionStart.current = [...orderRef.current];
		setDraggingId(id);
	}, []);

	const onItemDragEnd = useCallback(() => {
		setDraggingId(null);
		const start = dragSessionStart.current;
		dragSessionStart.current = null;
		if (!start) return;
		const cur = orderRef.current;
		if (cur.length === start.length && cur.some((v, i) => v !== start[i])) {
			setDragGate(true);
		}
	}, []);

	const handleReorder = (next: number[]) => {
		if (disabled) return;
		const prev = orderRef.current;
		if (prev.join(",") !== next.join(",")) {
			for (let i = 0; i < next.length; i++) {
				if (prev[i] !== next[i]) {
					setThudId(next[i]!);
					window.setTimeout(() => setThudId(null), 280);
					break;
				}
			}
		}
		orderRef.current = next;
		onOrderChange(next);
	};

	const onMoveToRankFromRow = (id: number, oneBased: number) => {
		if (disabled) return;
		const o = orderRef.current;
		const next = moveIdToOneBasedRank(o, id, oneBased);
		if (next.join(",") !== o.join(",")) {
			setKeyboardGate(true);
		}
		handleReorder(next);
	};

	const onMove = (id: number, dir: -1 | 1) => {
		if (disabled) return;
		const o = orderRef.current;
		const i = o.indexOf(id);
		const j = i + dir;
		if (j < 0 || j >= o.length) return;
		const prev = [...o];
		const next = [...o];
		[next[i]!, next[j]!] = [next[j]!, next[i]!];
		if (next.some((v, idx) => v !== prev[idx])) {
			setKeyboardGate(true);
		}
		handleReorder(next);
	};

	const n = options.length;
	const help = n >= 2 ? bordaPreviewSummary(n) : null;

	return (
		<div id="wi-ranked-ballot" className="space-y-3">
			<p className="sr-only">
				Ranked ballot: use arrow keys while a row is focused, J and K to move between rows, number keys 1 through 9 to
				jump the focused row to that rank, or Up and&nbsp;Down buttons, or drag the handle. Then move to cast vote.
			</p>
			<motion.div
				className="rounded-xl border border-wi-gold/20 bg-wi-surface/80 px-3.5 py-3 text-wi-text/65 text-xs shadow-wi-soft"
				initial={reduceMotion ? false : { opacity: 0, y: 6 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: reduceMotion ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }}
				aria-label="Keyboard shortcuts for ranked voting"
			>
				<span className="mr-2 font-medium text-wi-text/75">Keyboard path:</span>
				<span className="inline-flex flex-wrap gap-1.5 align-middle">
					{RANKED_KEY_CAPS.map((label) => (
						<kbd
							key={label}
							className="rounded-md border border-wi-text/12 bg-wi-bg px-2 py-1 font-sans font-medium text-[0.7rem] text-wi-text/75 shadow-sm"
						>
							{label}
						</kbd>
					))}
				</span>
			</motion.div>
			{help ? (
				<p className="inline-flex rounded-full border border-wi-gold/30 bg-wi-bg/80 px-3 py-1 text-wi-text/80 text-xs">
					<span className="font-medium text-wi-text">Score preview · </span>
					<span className="ml-1">{help}</span>
				</p>
			) : null}
			<Reorder.Group
				axis="y"
				values={order}
				onReorder={handleReorder}
				className="space-y-2"
				as="ul"
				onKeyDown={(e) => {
					if (disabled) return;
					const key = e.key;
					if (key !== "j" && key !== "J" && key !== "k" && key !== "K") return;
					if (!e.currentTarget.contains(e.target as Node)) return;
					const items = Array.from(
						e.currentTarget.querySelectorAll<HTMLElement>("li[tabindex='0']"),
					);
					const ae = document.activeElement as HTMLElement | null;
					const i = ae ? items.indexOf(ae) : -1;
					if (i < 0) return;
					if ((key === "j" || key === "J") && i < items.length - 1) {
						e.preventDefault();
						items[i + 1]?.focus();
					}
					if ((key === "k" || key === "K") && i > 0) {
						e.preventDefault();
						items[i - 1]?.focus();
					}
				}}
			>
				{order.map((id, index) => {
					const o = byId(id);
					if (!o) return null;
					return (
						<RankRow
							key={id}
							o={o}
							disabled={disabled}
							index={index}
							total={order.length}
							draggingId={draggingId}
							thudId={thudId}
							onItemDragStart={onItemDragStart}
							onItemDragEnd={onItemDragEnd}
							onMove={(dir) => onMove(id, dir)}
							reduceMotion={reduceMotion}
							onDigitRank={(r) => onMoveToRankFromRow(id, r)}
						/>
					);
				})}
			</Reorder.Group>
		</div>
	);
}

export function defaultRankOrder(options: Opt[]): number[] {
	return options.map((o) => o.id);
}

export function rankedPayloadFromOrder(order: number[], options: Opt[]): { kind: "ranked"; order: number[] } {
	const ids = new Set(options.map((o) => o.id));
	const seen = new Set<number>();
	const out: number[] = [];
	for (const id of order) {
		if (ids.has(id) && !seen.has(id)) {
			out.push(id);
			seen.add(id);
		}
	}
	for (const o of options) {
		if (!seen.has(o.id)) out.push(o.id);
	}
	return { kind: "ranked", order: out };
}
